import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { CircularProgress } from "@mui/material";
import StickyNote2Icon from "@mui/icons-material/StickyNote2";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PaidIcon from "@mui/icons-material/Paid";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import HistoryIcon from "@mui/icons-material/History";
import BdrButton from "../../../../components/BdrButton/BdrButton";
import BAGroupTimeFormat from "../../../../utils/formatters/BAGroupTimeFormat";
import { GET_CLIENT_ACTIVITY } from "../../../../utils/GraphQL/queries";
import "./clientActivityDisplay.css";

/**
 * The Activity panel: one merged, reverse-chronological feed of everything
 * that has happened on a client, assembled server-side by `getClientActivity`
 * from the records that already exist.
 *
 * LAZY AND PAGED. This is the least-opened tile and the most expensive to
 * assemble — it is a union across seven collections — so it fetches on mount
 * (i.e. when its tile is first selected) and never from the client query.
 * Paging is by CURSOR, not page number: the server hands back the timestamp of
 * the last row it returned and the next call asks for rows older than that.
 *
 * Rows carry `refType` + `refId`, which is what lets a click open the record
 * it describes without re-scanning the client payload to find it.
 */

const KIND_ICON = {
  quote_created: <RequestQuoteIcon />,
  quote_viewed: <VisibilityIcon />,
  invoice_created: <ReceiptLongIcon />,
  invoice_paid: <PaidIcon />,
  note_added: <StickyNote2Icon />,
  note_updated: <StickyNote2Icon />,
  task_created: <TaskAltIcon />,
  photo_uploaded: <PhotoLibraryIcon />,
  jobform_submitted: <AssignmentTurnedInIcon />,
  jobform_approved: <AssignmentTurnedInIcon />,
  ledger_job: <HistoryIcon />,
};

/*
 * The sub-line under each summary. It names the KIND; the summary says what
 * happened. Where the server's summary already carries the kind's own words
 * the label has to differ, or the row reads the same thing twice - which is
 * exactly what "Photo added / Photo added" and "Note added / Note added" did.
 */
const KIND_LABEL = {
  quote_created: "Quote",
  quote_viewed: "Client action",
  invoice_created: "Invoice",
  invoice_paid: "Payment",
  note_added: "Note",
  note_updated: "Note edited",
  task_created: "Task",
  photo_uploaded: "Client photo",
  jobform_submitted: "Job form",
  jobform_approved: "Job form",
  ledger_job: "From the ledger",
};

const PAGE_SIZE = 25;

const ClientActivityDisplay = ({ clientId, onOpenRecord }) => {
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);

  const [getActivity] = useLazyQuery(GET_CLIENT_ACTIVITY, {
    fetchPolicy: "network-only",
  });

  /*
   * Guards a late first page from overwriting a later one. The panel unmounts
   * when you leave the tile, so this is mostly belt-and-braces — but "Load
   * older" can be clicked twice before the first reply lands, and without a
   * sequence check the slower reply wins and the feed jumps backwards.
   */
  const requestSeq = useRef(0);

  const load = useCallback(
    async (fromCursor) => {
      if (!clientId) return;
      const seq = requestSeq.current + 1;
      requestSeq.current = seq;

      if (fromCursor) setLoadingMore(true);
      else setLoading(true);

      try {
        const { data } = await getActivity({
          variables: { clientId, cursor: fromCursor || null, limit: PAGE_SIZE },
        });
        if (seq !== requestSeq.current) return;

        const page = data?.getClientActivity;
        setItems((prev) => (fromCursor ? [...prev, ...(page?.items || [])] : page?.items || []));
        setCursor(page?.nextCursor || null);
        setError(false);
      } catch (err) {
        if (seq !== requestSeq.current) return;
        setError(true);
      } finally {
        if (seq === requestSeq.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [clientId, getActivity]
  );

  useEffect(() => {
    load(null);
  }, [load]);

  if (loading) {
    return (
      <div className="scc-list">
        <div className="scc-activity-loading">
          <CircularProgress color="inherit" size={20} />
          <span>Building activity…</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="scc-list">
        <div className="scc-list-empty">
          Could not load this client&apos;s activity.
          <BdrButton size="sm" variant="action" onClick={() => load(null)}>
            Try again
          </BdrButton>
        </div>
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className="scc-list">
        <div className="scc-list-empty">Nothing has happened on this client yet.</div>
      </div>
    );
  }

  /*
   * Month separators are computed as the list renders rather than stored on a
   * row: the server returns a flat stream, and which row STARTS a month
   * depends on what came before it in the merged order — which only the
   * client knows once a page has been appended.
   */
  let lastHeading = "";

  return (
    <div className="scc-list scc-activity">
      {items.map((item) => {
        const at = Number(item.at);
        const date = Number.isFinite(at) ? new Date(at) : null;
        const heading = date
          ? `${date.toLocaleString(undefined, { month: "long" })} ${date.getFullYear()}`
          : "";
        const showHeading = heading && heading !== lastHeading;
        if (showHeading) lastHeading = heading;

        const clickable = Boolean(item.refType && item.refId && onOpenRecord);
        const RowTag = clickable ? "button" : "div";

        return (
          <React.Fragment key={item.id}>
            {showHeading && (
              <div className="scc-activity-heading">
                <span>{heading}</span>
                <span className="scc-activity-rule" />
              </div>
            )}
            <RowTag
              type={clickable ? "button" : undefined}
              className={[
                "scc-activity-row",
                item.isClientAction ? "is-client" : "",
                /* Money landing is the one thing in this feed worth a
                   positive colour of its own. */
                item.kind === "invoice_paid" ? "is-paid" : "",
                clickable ? "is-clickable" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={
                clickable
                  ? () => onOpenRecord(item.refType, item.refId)
                  : undefined
              }
            >
              <span className="scc-activity-icon">
                {KIND_ICON[item.kind] || <HistoryIcon />}
              </span>
              <span className="scc-activity-body">
                <span className="scc-activity-summary">{item.summary}</span>
                <span className="scc-activity-meta">
                  {KIND_LABEL[item.kind] || item.kind}
                  {item.actorName ? ` · ${item.actorName}` : ""}
                  {item.dateIsApprox && (
                    <span className="bdr-badge bdr-badge--warning scc-activity-approx">
                      Date approx
                    </span>
                  )}
                </span>
              </span>
              <span className="scc-activity-date">
                {/* removeDayPrefix: the row is already in a dated group, so
                    "Tuesday" buys nothing and costs the width. */}
                {BAGroupTimeFormat(item.at, false, false, true) || "—"}
              </span>
            </RowTag>
          </React.Fragment>
        );
      })}

      {cursor && (
        <div className="scc-activity-more">
          <BdrButton
            size="sm"
            variant="action"
            disabled={loadingMore}
            onClick={() => load(cursor)}
          >
            {loadingMore ? (
              <CircularProgress color="inherit" size={12} />
            ) : (
              "Load older activity"
            )}
          </BdrButton>
        </div>
      )}
    </div>
  );
};

export default ClientActivityDisplay;
